/**
 * 一覧シートのヘッダ下に置くツールバー
 *
 * 何も選んでいないときは地図に出している件数だけを出し、選ぶとその場で操作に切り替わる。
 * 削除と共有は自分のプランにだけ効くので、自分のプランを選んでいないときは押せなくする。
 */

import { Eye, EyeOff, type LucideIcon, Share2, Trash2 } from 'lucide-react'

import { cn } from '@/lib/utils'

import { SelectBox } from './select-box'

type ToolbarButtonProps = {
  icon: LucideIcon
  label: string
  disabled?: boolean
  destructive?: boolean
  onClick: () => void
}

const ToolbarButton = ({ icon: Icon, label, disabled, destructive, onClick }: ToolbarButtonProps) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors',
      'hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40',
      destructive && 'hover:text-destructive',
    )}
  >
    <Icon size={14} />
    {label}
  </button>
)

type Props = {
  listedCount: number
  mapCount: number
  selectedCount: number
  /** 選んだうち自分のプランの件数 */
  ownSelectedCount: number
  isBusy: boolean
  onToggleAll: () => void
  onShow: () => void
  onHide: () => void
  onShare: () => void
  onDelete: () => void
}

export const PlanListToolbar = ({
  listedCount,
  mapCount,
  selectedCount,
  ownSelectedCount,
  isBusy,
  onToggleAll,
  onShow,
  onHide,
  onShare,
  onDelete,
}: Props) => {
  const checked = selectedCount === 0 ? false : selectedCount === listedCount ? true : 'mixed'

  return (
    <div className="flex h-10 items-center gap-1 border-b px-2">
      <SelectBox checked={checked} onToggle={onToggleAll} label={checked === true ? '選択を解除' : 'すべて選択'} />

      {selectedCount === 0 ? (
        <span className="flex items-center gap-1 pl-1 text-xs tabular-nums text-muted-foreground">
          <Eye size={13} />
          地図に {mapCount} / {listedCount} 件
        </span>
      ) : (
        <>
          <span className="shrink-0 pr-1 pl-1 text-xs font-medium tabular-nums text-primary">
            {selectedCount} 件選択
          </span>
          <span className="h-4 w-px shrink-0 bg-border" />
          <div className="flex min-w-0 items-center overflow-x-auto">
            <ToolbarButton icon={Eye} label="表示" onClick={onShow} />
            <ToolbarButton icon={EyeOff} label="隠す" onClick={onHide} />
            <ToolbarButton icon={Share2} label="共有" disabled={ownSelectedCount === 0 || isBusy} onClick={onShare} />
            <ToolbarButton
              icon={Trash2}
              label="削除"
              destructive
              disabled={ownSelectedCount === 0 || isBusy}
              onClick={onDelete}
            />
          </div>
        </>
      )}
    </div>
  )
}
